import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import FullScreenView from '../components/shared/FullScreenView';
import FullScreenTagPanel from '../components/admin/FullScreenTagPanel';
import ArtistStatusPanel from '../components/admin/ArtistStatusPanel';
import StatusMessage from '../components/shared/StatusMessage';
import ConfirmModal from '../components/shared/ConfirmModal';
import { authHeaders } from '../utils/auth';
import { apiFetch } from '../utils/api';
import { useBatch } from '../context/BatchContext';

// ── Helpers ───────────────────────────────────────────────────────────────────

const buildTagMap = (photos) => {
    const map = new Map();
    photos.forEach(photo => {
        if (!photo.isGroupPhoto && photo.credits?.length > 0) {
            const names = photo.credits
                .map(c => c.userId ? `${c.userId.fName} ${c.userId.lName.charAt(0)}.` : null)
                .filter(Boolean)
                .join(' ');
            if (names) map.set(photo.id, names);
        }
    });
    return map;
};

const SparklesIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 3L13.5 8.5L19 10L13.5 11.5L12 17L10.5 11.5L5 10L10.5 8.5L12 3Z" fill="currentColor" />
        <path d="M19 3L19.75 5.25L22 6L19.75 6.75L19 9L18.25 6.75L16 6L18.25 5.25L19 3Z" fill="currentColor" />
        <path d="M5 15L5.75 17.25L8 18L5.75 18.75L5 21L4.25 18.75L2 18L4.25 17.25L5 15Z" fill="currentColor" />
    </svg>
);

const getStatusBadge = (batch) => {
    if (batch.status === 'uploaded' && (batch.photoCount ?? 0) === 0) {
        return { label: 'Empty', cls: 'empty' };
    }
    return {
        uploaded:    { label: 'Awaiting AD Selection', cls: 'awaiting' },
        ad_selected: { label: 'Ready to Tag',          cls: 'ready' },
        pending:     { label: 'In Review',             cls: 'in-review' },
        processed:   { label: 'Processed',             cls: 'processed' },
    }[batch.status] ?? { label: batch.status, cls: '' };
};

// Approval view helpers (moved from ApprovalStatus.jsx)
const approvalBadgeClass = (finalStatus) => {
    switch (finalStatus) {
        case 'approved': return 'badge badge-success';
        case 'rejected': return 'badge badge-danger';
        case 'pending':  return 'badge badge-warning';
        case 'raw':      return 'badge badge-muted';
        default:         return 'badge';
    }
};

const creditBadgeClass = (status) => {
    switch (status) {
        case 'approved':      return 'credit-badge credit-badge-approved';
        case 'auto_approved': return 'credit-badge credit-badge-auto_approved';
        case 'rejected':      return 'credit-badge credit-badge-rejected';
        default:              return 'credit-badge credit-badge-pending';
    }
};

const formatCountdown = (endTime, state) => {
    if (state === 'not_started') return '—';
    if (!endTime || state === 'expired') return 'Expired';
    const ms = new Date(endTime).getTime() - Date.now();
    if (ms <= 0) return 'Expired';
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    if (h === 0 && m < 1) return '< 1m';
    if (h === 0) return `${m}m`;
    return `${h}h ${m}m`;
};

const notifShortLabel = (status) => {
    switch (status) {
        case 'sent':    return '✓';
        case 'failed':  return '⚠ failed';
        case 'bounced': return '⚠ bounced';
        case 'pending': return 'pending';
        default:        return '—';
    }
};

// ── Batch list sub-component ──────────────────────────────────────────────────

function BatchListView({ batches, navigate, refreshBatches, onSelectBatch }) {
    const [showForm, setShowForm] = useState(false);
    const [formData, setFormData] = useState({ season: '', photographer: '', production: '', description: '' });
    const [formError, setFormError] = useState('');
    const [deletingId, setDeletingId] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleteInput, setDeleteInput] = useState('');

    const initiateDelete = (batch) => {
        setDeleteTarget(batch);
        setDeleteInput('');
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        const id = deleteTarget.id;
        setDeletingId(id);
        setDeleteTarget(null);
        setDeleteInput('');
        try {
            const res = await apiFetch(`/api/batches/${id}`, { method: 'DELETE', headers: authHeaders() });
            if (res.ok) refreshBatches();
            else alert('Failed to delete batch.');
        } catch {
            alert('Failed to delete batch.');
        } finally {
            setDeletingId(null);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setFormError('');
        const res = await apiFetch('/api/batches', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...authHeaders() },
            body: JSON.stringify(formData),
        });
        if (res.ok) {
            const data = await res.json();
            navigate(`/admin/upload/${data.id}`);
        } else {
            setFormError('Failed to create batch');
        }
    };

return (
        <div className='batch-list'>
            <div className='batch-list-header'>
                <h2 className='batch-list-title'>Batches</h2>
                {!showForm && (
                    <button className='user-submit-btn' onClick={() => setShowForm(true)}>
                        + Upload New Batch
                    </button>
                )}
            </div>

            {showForm && (
                <form className='user-form' onSubmit={handleCreate}>
                    <h3 className='user-form-title'>New Batch</h3>
                    {formError && <p className='user-form-error'>{formError}</p>}
                    <div className='user-form-row'>
                        <div className='user-form-field'>
                            <label>Season</label>
                            <input
                                placeholder='e.g. 2025-2026'
                                value={formData.season}
                                onChange={e => setFormData(p => ({ ...p, season: e.target.value }))}
                                required
                            />
                        </div>
                        <div className='user-form-field'>
                            <label>Photographer</label>
                            <input
                                placeholder='Photographer name'
                                value={formData.photographer}
                                onChange={e => setFormData(p => ({ ...p, photographer: e.target.value }))}
                                required
                            />
                        </div>
                    </div>
                    <div className='user-form-row'>
                        <div className='user-form-field'>
                            <label>Production</label>
                            <input
                                placeholder='Production name'
                                value={formData.production}
                                onChange={e => setFormData(p => ({ ...p, production: e.target.value }))}
                                required
                            />
                        </div>
                        <div className='user-form-field'>
                            <label>Description <span style={{ fontWeight: 400, color: '#888' }}>(optional)</span></label>
                            <input
                                placeholder='Optional description'
                                value={formData.description}
                                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                            />
                        </div>
                    </div>
                    <div className='user-form-actions'>
                        <button type='button' className='user-cancel-btn' onClick={() => { setShowForm(false); setFormError(''); }}>
                            Cancel
                        </button>
                        <button type='submit' className='user-submit-btn'>Create Batch</button>
                    </div>
                </form>
            )}

            {batches.length === 0 ? (
                <p className='batch-list-empty'>No batches yet. Click "Upload New Batch" to get started.</p>
            ) : (
                <div className='table-responsive'>
                    <table className='batch-table'>
                        <thead>
                            <tr>
                                <th>Production</th>
                                <th>Season</th>
                                <th>Upload Date</th>
                                <th>Status</th>
                                <th>Add Photos</th>
                                <th>Delete</th>
                            </tr>
                        </thead>
                        <tbody>
                            {batches.map(batch => {
                                const { label, cls } = getStatusBadge(batch);
                                return (
                                    <tr
                                        key={batch.id}
                                        className='batch-table-row'
                                        onClick={() => onSelectBatch(batch)}
                                    >
                                        <td>{batch.production}</td>
                                        <td>{batch.season}</td>
                                        <td>{new Date(batch.createdAt).toLocaleDateString()}</td>
                                        <td>
                                            <span className={`batch-status-badge ${cls}`}>{label}</span>
                                            {batch.emailAlert && (
                                                <span className='batch-email-alert'>⚠ Email issue</span>
                                            )}
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            {batch.status === 'uploaded' && (
                                                <button
                                                    className='batch-action-btn'
                                                    onClick={() => navigate(`/admin/upload/${batch.id}`)}
                                                >
                                                    Add Photos
                                                </button>
                                            )}
                                        </td>
                                        <td onClick={e => e.stopPropagation()}>
                                            <button
                                                className='batch-action-btn danger'
                                                onClick={() => initiateDelete(batch)}
                                                disabled={deletingId === batch.id}
                                            >
                                                {deletingId === batch.id ? 'Deleting…' : 'Delete'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {deleteTarget && (
                <div className='delete-modal-backdrop'>
                    <div className='delete-modal'>
                        {deleteTarget.status === 'pending' && (
                            <p className='delete-modal-warning'>
                                ⚠️ This batch is currently in review. Artists may still be approving photos.
                            </p>
                        )}
                        <p className='delete-modal-body'>
                            This will permanently delete all photos and approval records for{' '}
                            <strong>{deleteTarget.production} — {deleteTarget.season}</strong>.
                        </p>
                        <p className='delete-modal-prompt'>
                            Type <strong>{deleteTarget.production}</strong> to confirm:
                        </p>
                        <input
                            className='delete-modal-input'
                            type='text'
                            value={deleteInput}
                            onChange={e => setDeleteInput(e.target.value)}
                            placeholder={deleteTarget.production}
                            autoFocus
                            onKeyDown={e => {
                                if (e.key === 'Enter' && deleteInput === deleteTarget.production) confirmDelete();
                                if (e.key === 'Escape') { setDeleteTarget(null); setDeleteInput(''); }
                            }}
                        />
                        <div className='delete-modal-actions'>
                            <button
                                className='user-cancel-btn'
                                onClick={() => { setDeleteTarget(null); setDeleteInput(''); }}
                            >
                                Cancel
                            </button>
                            <button
                                className='batch-action-btn danger'
                                onClick={confirmDelete}
                                disabled={deleteInput !== deleteTarget.production}
                            >
                                Delete Batch
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function Photos() {
    const navigate = useNavigate();
    const { batches, setBatches, batchId, setBatchId, selectedBatch, refreshBatches } = useBatch();

    // null = batch list, 'tagging' = tag view, 'approval' = approval view
    const [activeView, setActiveView] = useState(null);

    // Shared
    const [photos, setPhotos] = useState([]);
    const [inlineError, setInlineError] = useState('');
    const [modal, setModal] = useState(null);

    // Tagging state
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [tagMap, setTagMap] = useState(new Map());
    const [activePhotoId, setActivePhotoId] = useState(null);
    const [saveStatus, setSaveStatus] = useState('');
    const [filter, setFilter] = useState('all');
    const [tagPanelOpen, setTagPanelOpen] = useState(true);
    const [artists, setArtists] = useState([]);
    const [tagHistory, setTagHistory] = useState([]);
    const [artistQuery, setArtistQuery] = useState('');
    const [selectedArtists, setSelectedArtists] = useState([]);
    const [tagName, setTagName] = useState('');
    const [savingTag, setSavingTag] = useState(false);

    // Auto-tag state
    const [autoTag, setAutoTag] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);
    const cancelledRef = useRef(false);
    const pausePromiseRef = useRef(null);
    const pauseResolveRef = useRef(null);

    // Approval state
    const [artistStatus, setArtistStatus] = useState([]);
    const [photoCreditsMap, setPhotoCreditsMap] = useState(new Map());
    const [photoStatusMap, setPhotoStatusMap] = useState(new Map());
    const [loadState, setLoadState] = useState({ status: 'ready', error: null });
    const [committing, setCommitting] = useState(false);
    const [deletingRaw, setDeletingRaw] = useState(false);
    const [exporting, setExporting] = useState(false);
    const committedRef = useRef(false);
    const pollTimerRef = useRef(null);

    const batchIsCommitted = selectedBatch &&
        !['uploaded', 'ad_selected'].includes(selectedBatch?.status);

    const artistStatusMap = useMemo(
        () => new Map(artistStatus.map(a => [a.userId, a])),
        [artistStatus]
    );

    // Promise-based confirm modal
    const showConfirm = (opts) => new Promise(resolve => {
        setModal({
            ...opts,
            onConfirm: () => { setModal(null); resolve(true); },
            onCancel:  () => { setModal(null); resolve(false); },
        });
    });

    // ── Effects ───────────────────────────────────────────────────────────────

    useEffect(() => {
        if (activeView === null) refreshBatches();
    }, [activeView, refreshBatches]);

    useEffect(() => {
        Promise.all([
            apiFetch('/api/users?role=artist', { headers: authHeaders() }).then(r => r.json()),
            apiFetch('/api/compound-tags', { headers: authHeaders() }).then(r => r.json()),
        ]).then(([users, tags]) => {
            setArtists(Array.isArray(users) ? users : []);
            setTagHistory(Array.isArray(tags) ? tags : tags.tags ?? []);
        }).catch(() => {});
    }, []);

    // Load photos for tagging (isSelected only)
    useEffect(() => {
        if (!batchId || activeView !== 'tagging') return;
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => {
                const loaded = (Array.isArray(data) ? data : []).filter(p => p.isSelected);
                setPhotos(loaded);
                setTagMap(buildTagMap(loaded));
            })
            .catch(() => {});
    }, [batchId, activeView]);

    // Load all photos for approval view
    useEffect(() => {
        if (!batchId || activeView !== 'approval') return;
        setLoadState({ status: 'loading', error: null });
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => {
                setPhotos(Array.isArray(data) ? data : []);
                setLoadState({ status: 'ready', error: null });
            })
            .catch(err => setLoadState({ status: 'error', error: err.message }));
    }, [batchId, activeView]);

    // Deselect photos that fall outside the active filter
    useEffect(() => {
        if (activeView !== 'tagging') return;
        setSelectedIds(prev => {
            const next = new Set([...prev].filter(id => {
                const photo = photos.find(p => p.id === id);
                if (!photo) return false;
                const isTagged = tagMap.has(photo.id) || photo.isGroupPhoto;
                if (filter === 'tagged') return isTagged;
                if (filter === 'untagged') return !isTagged;
                return true;
            }));
            return next.size === prev.size ? prev : next;
        });
    }, [filter, photos, tagMap, activeView]);

    const fetchArtistStatus = useCallback(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/artist-status`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => setArtistStatus(Array.isArray(data) ? data : []))
            .catch(err => console.error('artist-status fetch failed:', err.message));
    }, [batchId]);

    const fetchPhotoCredits = useCallback(() => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/photo-credits`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => {
                const credits = data.credits ?? [];
                const statuses = data.photoStatuses ?? [];
                const credMap = new Map();
                for (const row of credits) credMap.set(`${row.photoId}-${row.userId}`, row.approvalStatus);
                setPhotoCreditsMap(credMap);
                const statusMap = new Map();
                for (const row of statuses) statusMap.set(row.photoId, row.finalStatus);
                setPhotoStatusMap(statusMap);
            })
            .catch(err => console.error('photo-credits fetch failed:', err.message));
    }, [batchId]);

    // Graduated polling for approval view.
    // If committedRef is true (just committed), uses fast schedule:
    //   5s × 3, 10s × 2, 15s × 2, 30s × 2, then 60s indefinitely.
    // Reasoning: email bounces can arrive within seconds; the old 60s poll
    // would leave a failed email showing "sent" for almost a full minute.
    useEffect(() => {
        if (activeView !== 'approval' || !batchId) return;

        const schedule = committedRef.current
            ? [5000, 5000, 5000, 10000, 10000, 15000, 15000, 30000, 30000]
            : [];
        let idx = 0;

        fetchArtistStatus();
        fetchPhotoCredits();

        const tick = () => {
            fetchArtistStatus();
            fetchPhotoCredits();
            const delay = idx < schedule.length ? schedule[idx++] : 60000;
            pollTimerRef.current = setTimeout(tick, delay);
        };

        const firstDelay = idx < schedule.length ? schedule[idx++] : 60000;
        pollTimerRef.current = setTimeout(tick, firstDelay);

        return () => {
            clearTimeout(pollTimerRef.current);
            committedRef.current = false;
        };
    }, [activeView, batchId, fetchArtistStatus, fetchPhotoCredits]);

    // ── State reset helpers ───────────────────────────────────────────────────

    const resetTaggingState = () => {
        setPhotos([]);
        setTagMap(new Map());
        setActivePhotoId(null);
        setFilter('all');
        setSelectedIds(new Set());
        setSelectedArtists([]);
        setArtistQuery('');
        setSaveStatus('');
        setTagPanelOpen(true);
    };

    const resetApprovalState = () => {
        setArtistStatus([]);
        setPhotoCreditsMap(new Map());
        setPhotoStatusMap(new Map());
        setLoadState({ status: 'ready', error: null });
        setCommitting(false);
        setDeletingRaw(false);
        setExporting(false);
    };

    // ── Batch list handlers ───────────────────────────────────────────────────

    const handleSelectBatch = (batch) => {
        setInlineError('');
        if (batch.status === 'uploaded') {
            navigate(`/admin/upload/${batch.id}`);
        } else if (batch.status === 'ad_selected') {
            setBatchId(String(batch.id));
            resetTaggingState();
            setActiveView('tagging');
        } else {
            // pending / processed → inline approval view
            setBatchId(String(batch.id));
            committedRef.current = false;
            resetApprovalState();
            setPhotos([]);
            setActiveView('approval');
        }
    };

    const handleBack = () => {
        clearTimeout(pollTimerRef.current);
        setBatchId('');
        setInlineError('');
        resetTaggingState();
        resetApprovalState();
        setActiveView(null);
    };

    // ── Tagging handlers ──────────────────────────────────────────────────────

    const togglePhoto = (photoId) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.has(photoId) ? next.delete(photoId) : next.add(photoId);
            return next;
        });
    };

    const toggleArtist = (artistId, checked) => {
        setSelectedArtists(prev =>
            checked ? [...prev, String(artistId)] : prev.filter(id => id !== String(artistId))
        );
        if (checked) setArtistQuery('');
    };

    const refreshPhotos = () => {
        if (!batchId) return;
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => {
                const loaded = (Array.isArray(data) ? data : []).filter(p => p.isSelected);
                const updatedMap = new Map(loaded.map(p => [p.id, p]));
                setPhotos(prev => prev.map(p => updatedMap.get(p.id) ?? p));
                setTagMap(buildTagMap(loaded));
            })
            .catch(() => {});
    };

    const refreshTagHistory = () => {
        apiFetch('/api/compound-tags', { headers: authHeaders() })
            .then(r => r.json())
            .then(data => setTagHistory(Array.isArray(data) ? data : data.tags ?? []))
            .catch(() => {});
    };

    const handleDeleteTag = async (tagId) => {
        try {
            const res = await apiFetch(`/api/compound-tags/${tagId}`, {
                method: 'DELETE',
                headers: authHeaders(),
            });
            if (!res.ok) throw new Error('Failed to delete tag');
            setTagHistory(prev => prev.filter(t => t.id !== tagId));
        } catch {
            setInlineError('Failed to remove tag.');
        }
    };

    const handleSaveTags = async () => {
        if (selectedIds.size === 0 || selectedArtists.length === 0) return;

        const photosToTag = [...selectedIds]
            .map(id => photos.find(p => p.id === id))
            .filter(Boolean);

        const tagRequests = photosToTag.map(photo => {
            const existingIds = (photo.credits ?? [])
                .filter(c => c.userId)
                .map(c => String(c.userId.id ?? c.userId));
            const merged = [...new Set([...existingIds, ...selectedArtists])];
            return { photoId: photo.id, mergedIds: merged };
        });

        const overLimit = tagRequests.filter(req => req.mergedIds.length > 2);

        if (overLimit.length > 0) {
            const n = overLimit.length;
            const ok = await showConfirm({
                title: 'Tag as Group Photos?',
                body: `${n} photo${n !== 1 ? 's' : ''} would have 3 or more tagged artists. Under the CBA, photos with 3+ artists are treated as group photos and bypass individual artist approval. Tag ${n === 1 ? 'it' : 'them'} as group photo${n !== 1 ? 's' : ''} instead?`,
                confirmLabel: 'Tag as Group',
            });
            if (!ok) return;

            const groupIds = new Set(overLimit.map(req => req.photoId));
            const individualRequests = tagRequests.filter(req => !groupIds.has(req.photoId));

            await Promise.all([...groupIds].map(photoId =>
                apiFetch(`/api/photos/${photoId}/group`, {
                    method: 'PATCH',
                    headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                    body: JSON.stringify({ isGroupPhoto: true }),
                })
            ));

            if (individualRequests.length > 0) {
                await Promise.all(individualRequests.map(({ photoId, mergedIds }) =>
                    apiFetch(`/api/photos/${photoId}/tags`, {
                        method: 'PATCH',
                        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                        body: JSON.stringify({ artistIds: mergedIds }),
                    })
                ));
            }

            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
            refreshTagHistory();
            setSelectedIds(new Set());
            setSelectedArtists([]);
            setArtistQuery('');
            refreshPhotos();
            return;
        }

        const results = await Promise.all(tagRequests.map(({ photoId, mergedIds }) =>
            apiFetch(`/api/photos/${photoId}/tags`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ artistIds: mergedIds }),
            }).then(r => ({ photoId, ok: r.ok }))
        ));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) setInlineError(`${failed.length} photo(s) failed to tag.`);

        if (results.some(r => r.ok)) {
            setSaveStatus('saved');
            setTimeout(() => setSaveStatus(''), 2000);
            refreshTagHistory();
        }

        setSelectedIds(new Set());
        setSelectedArtists([]);
        setArtistQuery('');
        refreshPhotos();
    };

    const handleClearTags = async () => {
        if (selectedIds.size === 0) return;
        const ok = await showConfirm({
            title: 'Clear Tags',
            body: `Remove tags from ${selectedIds.size} photo${selectedIds.size !== 1 ? 's' : ''}?`,
            confirmLabel: 'Clear Tags',
            isDestructive: true,
        });
        if (!ok) return;

        const results = await Promise.all([...selectedIds].map(photoId =>
            apiFetch(`/api/photos/${photoId}/tags`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ artistIds: [] }),
            }).then(r => ({ photoId, ok: r.ok }))
        ));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) setInlineError(`${failed.length} photo(s) failed to clear.`);
        setSelectedArtists([]);
        refreshPhotos();
    };

    const handleMarkAsGroup = async () => {
        if (selectedIds.size === 0) return;
        const ok = await showConfirm({
            title: 'Tag as Group Photos?',
            body: `Tag ${selectedIds.size} photo${selectedIds.size !== 1 ? 's' : ''} as group? This will delete all individual artist tags on those photos.`,
            confirmLabel: 'Tag as Group',
        });
        if (!ok) return;

        const results = await Promise.all([...selectedIds].map(photoId =>
            apiFetch(`/api/photos/${photoId}/group`, {
                method: 'PATCH',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ isGroupPhoto: true }),
            }).then(r => ({ photoId, ok: r.ok }))
        ));

        const failed = results.filter(r => !r.ok);
        if (failed.length > 0) setInlineError(`${failed.length} photo(s) failed.`);
        setSelectedIds(new Set());
        refreshPhotos();
    };

    const handleSaveTag = async () => {
        if (!tagName.trim() || selectedArtists.length === 0) return;
        setSavingTag(true);
        try {
            const res = await apiFetch('/api/compound-tags', {
                method: 'POST',
                headers: { ...authHeaders(), 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: tagName.trim(), artistIds: selectedArtists }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setInlineError(data.message || 'Failed to save tag');
                return;
            }
            setTagName('');
            setSelectedArtists([]);
            refreshTagHistory();
        } catch {
            setInlineError('Failed to save tag');
        } finally {
            setSavingTag(false);
        }
    };

    const handleCommit = async () => {
        if (!selectedBatch || !batchId) return;
        const ok = await showConfirm({
            title: 'Commit Batch?',
            body: `Commit "${selectedBatch.production} — ${selectedBatch.season}"? This will send approval links to all tagged artists and start the review window. This cannot be undone.`,
            confirmLabel: 'Commit Batch',
        });
        if (!ok) return;
        setCommitting(true);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/commit`, {
                method: 'POST',
                headers: authHeaders(),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                setInlineError(data.message || 'Failed to commit batch.');
                return;
            }
            setBatches(prev => prev.map(b =>
                String(b.id) === batchId ? { ...b, status: 'pending' } : b
            ));
            committedRef.current = true;
            setActiveView('approval');
        } catch {
            setInlineError('Network error — failed to commit batch.');
        } finally {
            setCommitting(false);
        }
    };

    // ── Fullscreen single-photo tagging ───────────────────────────────────────

    const handleFsSaveTags = async (photoId, artistIds) => {
        if (artistIds.length > 2) {
            const ok = await showConfirm({
                title: 'Tag as Group Photo?',
                body: `This photo would have ${artistIds.length} tagged artists. Under the CBA, photos with 3+ artists are treated as group photos and bypass individual artist approval. Tag it as a group photo instead?`,
                confirmLabel: 'Tag as Group',
            });
            if (!ok) return false;
            return handleFsMarkAsGroup(photoId, { confirmed: true });
        }
        const res = await apiFetch(`/api/photos/${photoId}/tags`, {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ artistIds }),
        });
        if (res.ok) { refreshTagHistory(); refreshPhotos(); }
        return res.ok;
    };

    const handleFsMarkAsGroup = async (photoId, { confirmed = false } = {}) => {
        if (!confirmed) {
            const ok = await showConfirm({
                title: 'Tag as Group Photo?',
                body: 'Tag this photo as group? This will delete all individual artist tags on this photo.',
                confirmLabel: 'Tag as Group',
            });
            if (!ok) return false;
        }
        const res = await apiFetch(`/api/photos/${photoId}/group`, {
            method: 'PATCH',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ isGroupPhoto: true }),
        });
        if (res.ok) { refreshTagHistory(); refreshPhotos(); }
        return res.ok;
    };

    const handleFsSaveNamedTag = async (name, artistIds) => {
        const res = await apiFetch('/api/compound-tags', {
            method: 'POST',
            headers: { ...authHeaders(), 'Content-Type': 'application/json' },
            body: JSON.stringify({ name, artistIds }),
        });
        if (res.ok) refreshTagHistory();
        return res.ok;
    };

    // ── Auto-tag ──────────────────────────────────────────────────────────────

    const runAutoTag = async (selectedPhotos) => {
        cancelledRef.current = false;
        pausePromiseRef.current = null;
        pauseResolveRef.current = null;
        setShowCancelConfirm(false);

        setAutoTag({
            open: true, running: true, done: false,
            total: selectedPhotos.length, currentIndex: 0,
            currentPhoto: selectedPhotos[0], currentStage: 'Starting…',
            tagCloud: new Map(), processed: 0, skipped: 0, groupCount: 0,
        });

        const tagCloudAcc = new Map();

        for (let i = 0; i < selectedPhotos.length; i++) {
            if (pausePromiseRef.current) await pausePromiseRef.current;
            if (cancelledRef.current) break;

            const photo = selectedPhotos[i];
            setAutoTag(prev => ({ ...prev, currentIndex: i, currentPhoto: photo, currentStage: 'Fetching image…' }));

            let result;
            try {
                setAutoTag(prev => ({ ...prev, currentStage: 'Analyzing faces with AI…' }));
                const res = await apiFetch(`/api/batches/${batchId}/ai-tag/${photo.id}`, {
                    method: 'POST', headers: authHeaders(),
                });
                result = await res.json();
            } catch {
                setAutoTag(prev => ({ ...prev, currentStage: 'Error — skipping' }));
                await new Promise(r => setTimeout(r, 600));
                setAutoTag(prev => ({ ...prev, skipped: (prev.skipped ?? 0) + 1, processed: i + 1 }));
                continue;
            }

            setAutoTag(prev => ({ ...prev, currentStage: 'Saving tags…' }));

            if (result.outcome === 'group') {
                const key = 'Group';
                tagCloudAcc.set(key, (tagCloudAcc.get(key) ?? 0) + 1);
                setAutoTag(prev => ({
                    ...prev,
                    groupCount: (prev.groupCount ?? 0) + 1,
                    tagCloud: new Map(tagCloudAcc),
                    currentStage: 'Tagged as group',
                    processed: i + 1,
                }));
            } else if (result.outcome === 'individual' && result.taggedUsers?.length > 0) {
                for (const user of result.taggedUsers) {
                    const name = `${user.fName} ${user.lName.charAt(0)}.`;
                    tagCloudAcc.set(name, (tagCloudAcc.get(name) ?? 0) + 1);
                }
                setAutoTag(prev => ({
                    ...prev,
                    tagCloud: new Map(tagCloudAcc),
                    currentStage: `Tagged: ${result.taggedUsers.map(u => `${u.fName} ${u.lName.charAt(0)}.`).join(', ')}`,
                    processed: i + 1,
                }));
            } else {
                setAutoTag(prev => ({
                    ...prev,
                    currentStage: result.outcome === 'no_faces' ? 'No faces detected' : 'Skipped',
                    skipped: (prev.skipped ?? 0) + 1,
                    processed: i + 1,
                }));
            }

            await new Promise(r => setTimeout(r, 300));
        }

        setAutoTag(prev => ({
            ...prev,
            running: false,
            done: true,
            currentStage: cancelledRef.current ? 'Cancelled' : 'Complete',
        }));
        setShowCancelConfirm(false);
        refreshPhotos();
        refreshTagHistory();
    };

    const handleStartAutoTag = async () => {
        const selectedPhotos = photos.filter(p => p.isSelected !== false);
        if (selectedPhotos.length === 0) return;
        const hasExisting = selectedPhotos.some(p => (p.credits?.length > 0) || p.isGroupPhoto);
        if (hasExisting) {
            const ok = await showConfirm({
                title: 'Replace Existing Tags?',
                body: 'Some photos already have tags. Running AI tagging will replace all existing tags for every photo in this batch. Continue?',
                confirmLabel: 'Continue',
                isDestructive: true,
            });
            if (!ok) return;
        }
        runAutoTag(selectedPhotos);
    };

    const handleCancelClick = () => {
        if (!autoTag?.running) return;
        let resolve;
        pausePromiseRef.current = new Promise(r => { resolve = r; });
        pauseResolveRef.current = resolve;
        setShowCancelConfirm(true);
    };

    const handleCancelConfirm = () => {
        cancelledRef.current = true;
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleCancelDeny = () => {
        pausePromiseRef.current = null;
        if (pauseResolveRef.current) pauseResolveRef.current();
        setShowCancelConfirm(false);
    };

    const handleCloseModal = () => {
        if (autoTag?.running) handleCancelClick();
        else setAutoTag(null);
    };

    // ── Approval handlers ─────────────────────────────────────────────────────

    const retryLoadApprovalPhotos = () => {
        setLoadState({ status: 'loading', error: null });
        apiFetch(`/api/batches/${batchId}/photos`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => {
                setPhotos(Array.isArray(data) ? data : []);
                setLoadState({ status: 'ready', error: null });
            })
            .catch(err => setLoadState({ status: 'error', error: err.message }));
    };

    const handleDeleteRaw = async () => {
        const count = deletableCount;
        const ok = await showConfirm({
            title: `Delete ${count} Photo${count !== 1 ? 's' : ''}?`,
            body: 'This will permanently remove all unselected and rejected photos from storage. This cannot be undone.',
            confirmLabel: 'Delete',
            isDestructive: true,
        });
        if (!ok) return;
        setDeletingRaw(true);
        try {
            const res = await apiFetch(
                `/api/batches/${batchId}/photos?finalStatus=raw&finalStatus=rejected`,
                { method: 'DELETE', headers: authHeaders() }
            );
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setInlineError(body.message || `Delete failed (${res.status})`);
                return;
            }
            const body = await res.json();
            setPhotos(prev => prev.filter(p =>
                p.isSelected && (photoStatusMap.get(p.id) ?? p.finalStatus) !== 'rejected'
            ));
            if (body.batchStatus) {
                setBatches(prev => prev.map(b =>
                    String(b.id) === batchId ? { ...b, status: body.batchStatus } : b
                ));
            }
            if (body.failed?.length) {
                setInlineError(`${body.deleted} deleted. ${body.failed.length} could not be removed — check server logs.`);
            }
        } catch (err) {
            setInlineError(`Delete failed: ${err.message}`);
        } finally {
            setDeletingRaw(false);
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const res = await apiFetch(`/api/batches/${batchId}/export`, { headers: authHeaders() });
            if (!res.ok) {
                const body = await res.json().catch(() => ({}));
                setInlineError(body.message || `Export failed (${res.status})`);
                return;
            }
            const blob = await res.blob();
            const objectURL = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectURL;
            a.download = `${selectedBatch.production}_approved.zip`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectURL);
        } catch (err) {
            setInlineError(`Export failed: ${err.message}`);
        } finally {
            setExporting(false);
        }
    };

    const handleResend = useCallback(async (linkId) => {
        const res = await apiFetch(`/api/shared-links/${linkId}/resend`, {
            method: 'POST', headers: authHeaders(),
        });
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            throw new Error(body.message || `Resend failed (${res.status})`);
        }
        fetchArtistStatus();
        fetchPhotoCredits();
    }, [fetchArtistStatus, fetchPhotoCredits]);

    // ── Derived values ────────────────────────────────────────────────────────

    const filteredArtists = artistQuery
        ? artists.filter(a =>
            `${a.fName} ${a.lName}`.toLowerCase().includes(artistQuery.toLowerCase())
          )
        : [];

    const unselectedMatches = filteredArtists.filter(a => !selectedArtists.includes(String(a.id)));

    const visiblePhotos = photos.filter(photo => {
        const isTagged = tagMap.has(photo.id) || photo.isGroupPhoto;
        if (filter === 'tagged') return isTagged;
        if (filter === 'untagged') return !isTagged;
        return true;
    });

    const activePhoto = activePhotoId ? photos.find(p => p.id === activePhotoId) : null;
    const activeIdx = activePhoto ? photos.indexOf(activePhoto) : -1;

    const taggedCount   = photos.filter(p => tagMap.has(p.id) || p.isGroupPhoto).length;
    const untaggedCount = photos.length - taggedCount;
    const allTagged     = photos.length > 0 && untaggedCount === 0;
    const isCommitted   = batchIsCommitted;
    const allVisibleSelected = visiblePhotos.length > 0 && visiblePhotos.every(p => selectedIds.has(p.id));
    const someSelectedHaveTags = [...selectedIds].some(id => {
        const photo = photos.find(p => p.id === id);
        return photo && (tagMap.has(photo.id) || photo.isGroupPhoto);
    });

    const deletableCount = photos.filter(p => {
        const status = photoStatusMap.get(p.id) ?? p.finalStatus;
        return (!p.isSelected && batchIsCommitted) || status === 'rejected';
    }).length;

    const approvedCount = photos.filter(p =>
        (photoStatusMap.get(p.id) ?? p.finalStatus) === 'approved'
    ).length;

    // ── Render: batch list ────────────────────────────────────────────────────

    if (activeView === null) {
        return (
            <BatchListView
                batches={batches}
                navigate={navigate}
                refreshBatches={refreshBatches}
                onSelectBatch={handleSelectBatch}
            />
        );
    }

    // ── Render: approval view ─────────────────────────────────────────────────

    if (activeView === 'approval') {
        return (
            <div className='approval-status'>
                <div className='approval-status-header'>
                    <button className='back-link' onClick={handleBack}>← Batches</button>
                    {selectedBatch && (
                        <span className='photos-batch-info'>
                            {selectedBatch.production} · {selectedBatch.season}
                        </span>
                    )}
                </div>

                {inlineError && (
                    <div className='inline-error-banner'>
                        {inlineError}
                        <button className='inline-error-dismiss' onClick={() => setInlineError('')}>×</button>
                    </div>
                )}

                {selectedBatch && photos.length > 0 && (
                    <div className='approval-status-actions'>
                        {deletableCount > 0 && (
                            <button className='btn-danger-outline' onClick={handleDeleteRaw} disabled={deletingRaw}>
                                {deletingRaw ? 'Deleting…' : `Delete Unselected & Rejected (${deletableCount})`}
                            </button>
                        )}
                        {approvedCount > 0 && (
                            <button className='app-nav-btn' onClick={handleExport} disabled={exporting}>
                                {exporting ? 'Preparing ZIP…' : `Download Approved ZIP (${approvedCount})`}
                            </button>
                        )}
                    </div>
                )}

                {loadState.status !== 'ready' && (
                    <StatusMessage state={loadState} onRetry={retryLoadApprovalPhotos} />
                )}
                {loadState.status === 'ready' && photos.length === 0 && (
                    <p>No photos in this batch yet.</p>
                )}
                {loadState.status === 'ready' && photos.length > 0 && (
                    <div className='photo-grid'>
                        {photos.map(photo => (
                            <ApprovalPhotoCard
                                key={photo.id}
                                photo={photo}
                                photoStatusMap={photoStatusMap}
                                photoCreditsMap={photoCreditsMap}
                                artistStatusMap={artistStatusMap}
                                batchIsCommitted={batchIsCommitted}
                            />
                        ))}
                    </div>
                )}

                {batchIsCommitted && selectedBatch && (
                    <ArtistStatusPanel artistStatus={artistStatus} onResend={handleResend} />
                )}

                {modal && <ConfirmModal {...modal} />}
            </div>
        );
    }

    // ── Render: tagging view ──────────────────────────────────────────────────

    return (
        <div className='tag-photos-controls'>
            <div className='tag-photos-header'>
                <button className='back-link' onClick={handleBack}>← Batches</button>
                {selectedBatch && (
                    <span className='photos-batch-info'>
                        {selectedBatch.production} · {selectedBatch.season}
                    </span>
                )}
            </div>

            {inlineError && (
                <div className='inline-error-banner'>
                    {inlineError}
                    <button className='inline-error-dismiss' onClick={() => setInlineError('')}>×</button>
                </div>
            )}

            {isCommitted && (
                <p className='tag-committed-notice'>
                    This batch has been committed and can no longer be tagged.
                </p>
            )}

            {!isCommitted && (
                <div className={`tag-artist-panel${tagPanelOpen ? '' : ' collapsed'}`}>
                    <button className='tag-panel-toggle' onClick={() => setTagPanelOpen(o => !o)}>
                        <span className='tag-panel-toggle-label'>Tagging {tagPanelOpen ? '▼' : '▲'}</span>
                    </button>

                    {tagPanelOpen && (
                        <>
                            <div className='tag-recent-col'>
                                {tagHistory.length > 0 && (
                                    <>
                                        <span className='tag-history-label'>Recent:</span>
                                        <div className='tag-history-row'>
                                            {tagHistory.map(tag => {
                                                const tagArtistIds = tag.artistIds.map(a => String(a.id));
                                                const isSingle = tagArtistIds.length === 1;
                                                const isActive = isSingle
                                                    ? selectedArtists.includes(tagArtistIds[0])
                                                    : tagArtistIds.length === selectedArtists.length &&
                                                      tagArtistIds.every(id => selectedArtists.includes(id));
                                                const handleClick = () => {
                                                    if (isSingle) {
                                                        const id = tagArtistIds[0];
                                                        setSelectedArtists(prev =>
                                                            prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]
                                                        );
                                                    } else if (isActive) {
                                                        setSelectedArtists([]);
                                                    } else {
                                                        setSelectedArtists(tagArtistIds);
                                                    }
                                                    setArtistQuery('');
                                                };
                                                return (
                                                    <span key={tag.id} className={`tag-history-chip${isActive ? ' active' : ''}`}>
                                                        <button className='tag-history-btn' onClick={handleClick}>{tag.name}</button>
                                                        <button className='tag-history-delete' title='Remove this tag' onClick={() => handleDeleteTag(tag.id)}>×</button>
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </>
                                )}
                            </div>

                            <div className='tag-right-section'>
                                {(selectedArtists.length > 0 || selectedIds.size > 0) && (
                                    <div className='tag-row-1'>
                                        {selectedArtists.length > 0 && (
                                            <div className='tag-selected-row'>
                                                {selectedArtists.map(id => {
                                                    const artist = artists.find(a => String(a.id) === id);
                                                    return artist ? (
                                                        <label key={id} className='artist-check-row selected'>
                                                            <input type='checkbox' checked={true} onChange={() => toggleArtist(id, false)} />
                                                            {artist.fName} {artist.lName}
                                                        </label>
                                                    ) : null;
                                                })}
                                            </div>
                                        )}
                                        <div className='tag-action-row'>
                                            <div className='tag-primary-row'>
                                                {selectedIds.size > 0 && (
                                                    <button className='tag-save-btn tag-group-btn' onClick={handleMarkAsGroup}>
                                                        Tag as Group
                                                    </button>
                                                )}
                                                {selectedIds.size > 0 && selectedArtists.length > 0 && (
                                                    <button className='tag-save-btn' onClick={handleSaveTags}>
                                                        Tag {selectedIds.size} Photo{selectedIds.size !== 1 ? 's' : ''} — {selectedArtists.length} Artist{selectedArtists.length !== 1 ? 's' : ''} Selected
                                                    </button>
                                                )}
                                                {photos.length > 0 && (
                                                    <button className='ai-tag-panel-btn' onClick={handleStartAutoTag}>
                                                        <SparklesIcon />
                                                        Auto Tag with AI
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}

                                <div className='tag-row-2'>
                                    <div className='tag-search-row'>
                                        <input
                                            type='search'
                                            className='artist-search'
                                            placeholder='Search artists…'
                                            value={artistQuery}
                                            onChange={e => setArtistQuery(e.target.value)}
                                        />
                                        {artistQuery && (
                                            filteredArtists.length === 0
                                                ? <span className='artist-no-results'>No artists found</span>
                                                : unselectedMatches.map(a => (
                                                    <label key={a.id} className='artist-check-row'>
                                                        <input type='checkbox' checked={false} onChange={e => toggleArtist(a.id, e.target.checked)} />
                                                        {a.fName} {a.lName}
                                                    </label>
                                                ))
                                        )}
                                        {saveStatus === 'saved' && (
                                            <div className='tag-save-confirmation'>✓ Tags saved</div>
                                        )}
                                    </div>
                                    {selectedArtists.length > 0 && (
                                        <div className='tag-name-group'>
                                            <input
                                                type='text'
                                                className='tag-name-input'
                                                placeholder='Save as named tag…'
                                                value={tagName}
                                                onChange={e => setTagName(e.target.value)}
                                                onKeyDown={e => e.key === 'Enter' && handleSaveTag()}
                                            />
                                            <button
                                                className='tag-save-tag-btn'
                                                onClick={handleSaveTag}
                                                disabled={savingTag || !tagName.trim()}
                                            >
                                                {savingTag ? 'Saving…' : 'Save Tag'}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                <div className='tag-row-3'>
                                    <div className='tag-utility-row'>
                                        {!allVisibleSelected && (
                                            <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set(visiblePhotos.map(p => p.id)))}>
                                                Select All ({visiblePhotos.length})
                                            </button>
                                        )}
                                        {selectedIds.size > 0 && (
                                            <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set())}>
                                                Clear Selection ({selectedIds.size})
                                            </button>
                                        )}
                                        {someSelectedHaveTags && (
                                            <button className='tag-clear-btn' onClick={handleClearTags}>Clear Tags</button>
                                        )}
                                    </div>
                                    <div className='tag-filter-inline'>
                                        <button className={filter === 'all' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('all')}>
                                            All ({photos.length})
                                        </button>
                                        <button className={filter === 'untagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('untagged')}>
                                            Untagged ({untaggedCount})
                                        </button>
                                        <button className={filter === 'tagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('tagged')}>
                                            Tagged ({taggedCount})
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}

            {!isCommitted && (
                <div className='tag-filter-tabs'>
                    <button className={filter === 'all' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('all')}>All ({photos.length})</button>
                    <button className={filter === 'untagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('untagged')}>Untagged ({untaggedCount})</button>
                    <button className={filter === 'tagged' ? 'tag-filter-btn active' : 'tag-filter-btn'} onClick={() => setFilter('tagged')}>Tagged ({taggedCount})</button>
                </div>
            )}

            {!isCommitted && (
                <div className='tag-grid-utility-row'>
                    {!allVisibleSelected && (
                        <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set(visiblePhotos.map(p => p.id)))}>Select All ({visiblePhotos.length})</button>
                    )}
                    {selectedIds.size > 0 && (
                        <button className='tag-clear-btn' onClick={() => setSelectedIds(new Set())}>Clear Selection ({selectedIds.size})</button>
                    )}
                    {someSelectedHaveTags && (
                        <button className='tag-clear-btn' onClick={handleClearTags}>Clear Tags</button>
                    )}
                </div>
            )}

            {!isCommitted && photos.length > 0 && (
                <div className='ai-tag-btn-wrapper'>
                    <button className='ai-tag-btn' onClick={handleStartAutoTag}>
                        <SparklesIcon />
                        Auto Tag with AI
                    </button>
                </div>
            )}

            {allTagged && !isCommitted && (
                <div className='tag-all-tagged'>
                    <span>✓ All photos tagged.</span>
                    <button className='tag-commit-btn' onClick={handleCommit} disabled={committing}>
                        {committing ? 'Committing…' : 'Commit Batch'}
                    </button>
                </div>
            )}

            {visiblePhotos.length > 0 && (
                <div className='tag-photo-grid'>
                    {visiblePhotos.map(photo => (
                        <TagPhotoCard
                            key={photo.id}
                            photo={photo}
                            isChecked={selectedIds.has(photo.id)}
                            taggedNames={tagMap.get(photo.id) ?? null}
                            onCheck={() => togglePhoto(photo.id)}
                            onView={() => setActivePhotoId(photo.id)}
                        />
                    ))}
                </div>
            )}

            {photos.length > 0 && visiblePhotos.length === 0 && (
                <p className='tag-empty-filter'>No photos match this filter.</p>
            )}

            <FullScreenView
                photo={activePhoto}
                onClose={() => setActivePhotoId(null)}
                onPrev={() => activeIdx > 0 && setActivePhotoId(photos[activeIdx - 1].id)}
                onNext={() => activeIdx < photos.length - 1 && setActivePhotoId(photos[activeIdx + 1].id)}
                hasPrev={activeIdx > 0}
                hasNext={activeIdx !== -1 && activeIdx < photos.length - 1}
                actionArea={
                    activePhoto && !isCommitted ? (
                        <FullScreenTagPanel
                            key={activePhoto.id}
                            photo={activePhoto}
                            artists={artists}
                            tagHistory={tagHistory}
                            onSaveTags={handleFsSaveTags}
                            onMarkAsGroup={handleFsMarkAsGroup}
                            onSaveNamedTag={handleFsSaveNamedTag}
                            onDeleteTag={handleDeleteTag}
                        />
                    ) : null
                }
            />

            {autoTag?.open && (
                <AutoTagModal
                    state={autoTag}
                    showCancelConfirm={showCancelConfirm}
                    onCancelClick={handleCancelClick}
                    onCancelConfirm={handleCancelConfirm}
                    onCancelDeny={handleCancelDeny}
                    onClose={handleCloseModal}
                />
            )}

            {modal && <ConfirmModal {...modal} />}
        </div>
    );
}
    // ── ApprovalPhotoCard ─────────────────────────────────────────────────────────

function ApprovalPhotoCard({ photo, photoStatusMap, photoCreditsMap, artistStatusMap, batchIsCommitted }) {
    const status = photoStatusMap.get(photo.id) ?? photo.finalStatus ?? 'raw';
    const isUnselected = !photo.isSelected;
    const credits = (photo.credits ?? []).filter(c => c.userId);

    return (
        <div className={`photo-card${isUnselected ? ' photo-card-unselected' : ''}`}>
            <div className='photo-img-wrapper'>
                <img
                    src={photo.thumbnailURL ?? photo.displayURL}
                    alt={photo.originalFilename}
                />
                {batchIsCommitted && !isUnselected && (
                    <span className={`photo-status-badge ${approvalBadgeClass(status)}`}>
                        {status}
                    </span>
                )}
            </div>

            <div className='photo-meta'>
                <div className='photo-meta-row'>
                    <span className='photo-filename' title={photo.originalFilename}>
                        {photo.originalFilename}
                    </span>
                    {isUnselected && (
                        <span className='badge badge-unselected'>Not Selected</span>
                    )}
                    {!batchIsCommitted && !isUnselected && (
                        <span className={approvalBadgeClass(status)}>{status}</span>
                    )}
                </div>

                {!batchIsCommitted && (credits.length > 0 || photo.isGroupPhoto) && (
                    <div className='photo-tags'>
                        {photo.isGroupPhoto && (
                            <span className='photo-tag-pill group'>Group Photo</span>
                        )}
                        {credits.map((c, i) => (
                            <span key={i} className='photo-tag-pill'>
                                {c.userId.fName} {c.userId.lName}
                            </span>
                        ))}
                    </div>
                )}

                {batchIsCommitted && !isUnselected && (
                    <div className='photo-credit-rows'>
                        {photo.isGroupPhoto && (
                            <div className='photo-credit-row'>
                                <span className='photo-credit-name'>Group Photo</span>
                            </div>
                        )}
                        {credits.map((credit, i) => {
                            const userId = credit.userId.id;
                            const artist = artistStatusMap.get(userId);
                            const name = `${credit.userId.fName} ${credit.userId.lName}`;
                            const notifStatus = artist?.notificationStatus ?? null;
                            const timer = artist?.timer ?? { state: 'not_started', endTime: null };
                            const isNotNotified = (notifStatus === 'failed' || notifStatus === 'bounced') && timer.state === 'not_started';
                            const liveStatus = photoCreditsMap.get(`${photo.id}-${userId}`) ?? credit.approvalStatus;
                            return (
                                <div key={i} className='photo-credit-row'>
                                    <span className='photo-credit-name'>{name}</span>
                                    <span className='photo-credit-sep'>·</span>
                                    <span className={`photo-credit-notif${(notifStatus === 'failed' || notifStatus === 'bounced') ? ' photo-credit-notif-failed' : ''}`}>
                                        {notifShortLabel(notifStatus)}
                                    </span>
                                    <span className='photo-credit-sep'>·</span>
                                    <span className='photo-credit-timer'>
                                        {formatCountdown(timer.endTime, timer.state)}
                                    </span>
                                    <span className='photo-credit-sep'>·</span>
                                    {isNotNotified ? (
                                        <span className='credit-badge credit-badge-not-notified'>Not notified</span>
                                    ) : (
                                        <span className={creditBadgeClass(liveStatus)}>{liveStatus}</span>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
}

// ── TagPhotoCard ──────────────────────────────────────────────────────────────

function TagPhotoCard({ photo, isChecked, taggedNames, onCheck, onView }) {
    let borderColor = 'var(--color-border)';
    let badgeText = 'Untagged';
    let badgeClass = 'tag-badge untagged';

    if (photo.isGroupPhoto) {
        borderColor = 'var(--color-accent)';
        badgeText = 'Group';
        badgeClass = 'tag-badge group';
    } else if (taggedNames) {
        borderColor = 'var(--color-primary)';
        badgeText = taggedNames;
        badgeClass = 'tag-badge individual';
    }

    return (
        <div
            className={`tag-photo-card${isChecked ? ' checked' : ''}`}
            style={{ borderColor }}
            onClick={onCheck}
        >
            <div className='tag-photo-img'>
                <img src={photo.thumbnailURL ?? photo.displayURL} alt={photo.originalFilename} />
                <button
                    className='tag-photo-view-btn'
                    title='View full size'
                    onClick={e => { e.stopPropagation(); onView(); }}
                >
                    ⛶
                </button>
            </div>
            <div className='tag-photo-footer' onClick={e => e.stopPropagation()}>
                <label className='tag-photo-check'>
                    <input type='checkbox' checked={!!isChecked} onChange={onCheck} />
                </label>
                <span className={badgeClass}>{badgeText}</span>
            </div>
        </div>
    );
}

// ── AutoTagModal ──────────────────────────────────────────────────────────────

function AutoTagModal({ state, showCancelConfirm, onCancelClick, onCancelConfirm, onCancelDeny, onClose }) {
    const { running, done, total, currentIndex, currentPhoto, currentStage, tagCloud, processed, skipped, groupCount } = state;

    const progress = total > 0 ? Math.round(((processed ?? currentIndex) / total) * 100) : 0;
    const tagEntries = tagCloud ? [...tagCloud.entries()].sort((a, b) => b[1] - a[1]) : [];
    const countLabel = done
        ? `${processed ?? 0} of ${total} processed`
        : `${(currentIndex ?? 0) + 1} of ${total}`;

    return (
        <div className='ai-modal-backdrop' onClick={e => { if (e.target === e.currentTarget && !running) onClose(); }}>
            <div className='ai-modal' role='dialog' aria-modal='true' aria-label='Auto Tag with AI'>
                <div className='ai-modal-header'>
                    <h2 className='ai-modal-title'>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M12 3L13.5 8.5L19 10L13.5 11.5L12 17L10.5 11.5L5 10L10.5 8.5L12 3Z" fill="currentColor" />
                            <path d="M19 3L19.75 5.25L22 6L19.75 6.75L19 9L18.25 6.75L16 6L18.25 5.25L19 3Z" fill="currentColor" />
                            <path d="M5 15L5.75 17.25L8 18L5.75 18.75L5 21L4.25 18.75L2 18L4.25 17.25L5 15Z" fill="currentColor" />
                        </svg>
                        Auto Tag with AI
                    </h2>
                    {!running && (
                        <button className='ai-modal-close' onClick={onClose} aria-label='Close'>×</button>
                    )}
                </div>

                {currentPhoto && (
                    <div className='ai-modal-current'>
                        <div className='ai-modal-thumb'>
                            <img src={currentPhoto.thumbnailURL ?? currentPhoto.displayURL} alt={currentPhoto.originalFilename} />
                        </div>
                        <div className='ai-modal-photo-info'>
                            <div className='ai-modal-filename'>{currentPhoto.originalFilename}</div>
                            <div className={`ai-modal-stage${done ? ' done' : ''}`}>{currentStage}</div>
                        </div>
                    </div>
                )}

                <div className='ai-modal-progress-row'>
                    <span className='ai-modal-count'>{countLabel}</span>
                </div>
                <div className='ai-modal-progress-bar-track'>
                    <div className='ai-modal-progress-bar-fill' style={{ width: `${done ? 100 : progress}%` }} />
                </div>

                {tagEntries.length > 0 && (
                    <div className='ai-modal-cloud-section'>
                        <div className='ai-modal-cloud-label'>Tags used</div>
                        <div className='ai-modal-cloud'>
                            {tagEntries.map(([name, count]) => (
                                <span key={name} className={`ai-cloud-chip${name === 'Group' ? ' group' : ''}`}>
                                    {name}
                                    <span className='ai-cloud-count'>{count}</span>
                                </span>
                            ))}
                        </div>
                    </div>
                )}

                {done && (
                    <div className='ai-modal-summary'>
                        <div className='ai-modal-summary-item'>
                            <span className='ai-summary-value'>{processed ?? 0}</span>
                            <span className='ai-summary-label'>photos processed</span>
                        </div>
                        <div className='ai-modal-summary-item'>
                            <span className='ai-summary-value'>{groupCount ?? 0}</span>
                            <span className='ai-summary-label'>marked as group</span>
                        </div>
                        <div className='ai-modal-summary-item'>
                            <span className='ai-summary-value'>{tagEntries.filter(([n]) => n !== 'Group').length}</span>
                            <span className='ai-summary-label'>unique artists tagged</span>
                        </div>
                        {(skipped ?? 0) > 0 && (
                            <div className='ai-modal-summary-item'>
                                <span className='ai-summary-value'>{skipped}</span>
                                <span className='ai-summary-label'>skipped / errors</span>
                            </div>
                        )}
                    </div>
                )}

                {showCancelConfirm && (
                    <div className='ai-cancel-confirm'>
                        <p>Stop processing? Tags saved so far will be kept.</p>
                        <div className='ai-cancel-confirm-btns'>
                            <button className='ai-cancel-deny-btn' onClick={onCancelDeny}>Keep going</button>
                            <button className='ai-cancel-confirm-btn' onClick={onCancelConfirm}>Stop</button>
                        </div>
                    </div>
                )}

                <div className='ai-modal-footer'>
                    {running && !showCancelConfirm && (
                        <button className='ai-modal-cancel-btn' onClick={onCancelClick}>Cancel</button>
                    )}
                    {done && (
                        <button className='ai-modal-done-btn' onClick={onClose}>Done</button>
                    )}
                </div>
            </div>
        </div>
    );
}